// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardActions,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {
  ButtonTextMedium,
  SubTitleExtraSmall,
  SubTitleLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import User from 'src/components/crm/lib/user/user.jsx';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
// $FlowFixMe[untyped-import]
import {formatMomentDateAndTime} from 'src/utils/date-time';
import css from './approvals-card.css';


const getApprovalType = (approval) => {
  if (approval.offerLetterId) {
    return 'OFFER_LETTER';
  }
  return 'JOB';
};

const getChipProps = (approvalType, t) => {
  const props = {
    semantic: approvalType === 'JOB' ? 'information' : 'warning',
    innerText:
      approvalType === 'JOB'
        ? t('Job_Approval', 'Job Approval')
        : t('Offer_Approval', 'Offer Approval'),
  };
  return props;
};

type PendingApproval = {
  id: string,
  jobTitle: string,
  createdOn: number,
  updatedOn?: number,
  offerLetterId?: string,
  candidateName?: string,
  stepId?: string,
  candidateId?: string,
  status: string,
  approverId?: string,
  jobId: string,
  stageId?: string,
  updatedBy?: string,
};

type Props = {
  pendingapproval: PendingApproval,
  handleClick: () => void,
};

export const ApprovalCard = ({
  pendingapproval,
  handleClick,
}: Props): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const approvalType = getApprovalType(pendingapproval);
  const createdDate = formatMomentDateAndTime(pendingapproval.updatedOn);
  const chipProps = getChipProps(approvalType, t);
  const renderCandidateName = () => {
    if (approvalType !== 'JOB' && pendingapproval.candidateName) {
      return (
        <SubTitleExtraSmall className={css.requestInfo}>
          <Icon name="user" size="small" type="regular" />
          {`${t('Candidate', 'Candidate')}: ${
            pendingapproval.candidateName ?? ''
          }`}
        </SubTitleExtraSmall>
      );
    }
    return null;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return 'clock-rotate-left';
      case 'REJECTED':
        return 'times-circle';
      case 'APPROVED':
        return 'check-circle';
      default:
        return 'clock-rotate-left';
    }
  };

  const renderStatusInfo = () => {
    const status = pendingapproval.status;
    const statusText =
      {
        PENDING: t('Requested_by', 'Requested by'),
        REJECTED: t('Rejected_by', 'Rejected by'),
        APPROVED: t('Approved_by', 'Approved by'),
      }[status] || t('Requested_by', 'Requested by');

    const color =
      {
        PENDING: '',
        REJECTED: 'danger',
        APPROVED: 'success',
      }[status] || '';

    return (
      <SubTitleExtraSmall className={css.requestInfo}>
        <Icon
          name={getStatusIcon(status)}
          size="small"
          type="solid"
          color={color}
        />
        {`${statusText} `}
        {pendingapproval.updatedBy &&
          (status === 'PENDING' ? (
            <User userId={parseInt(pendingapproval.updatedBy, 10)} />
          ) : (
            <User userId={parseInt(pendingapproval.approverId, 10)} />
          ))}
        {`${t('on', 'on')} ${createdDate}`}
      </SubTitleExtraSmall>
    );
  };

  return (
    <Card
      classNames={{
        wrapper: css.card,
      }}
    >
      <div className={css.cardHeader}>
        <ButtonTextMedium className={css.gap}>
          {pendingapproval.jobTitle}
          <Chip semantic={chipProps.semantic}>{chipProps.innerText}</Chip>
        </ButtonTextMedium>
        <CardActions>
          <Button
            children={
              pendingapproval.status === 'PENDING'
                ? t('Review_and_Approve', 'Review and Approve')
                : t('View_Details', 'View Details')
            }
            onClick={handleClick}
            type="tertiary"
            size="small"
            iconLeftName={
              pendingapproval.status === 'PENDING' ? 'file-check' : ''
            }
          />
        </CardActions>
      </div>
      <CardContent className={css.innerCard}>
        <Card
          classNames={{
            wrapper: css.innerApprovalPendingCard,
          }}
          paddingTop="small"
          paddingBottom="small"
        >
          {renderStatusInfo()}
          {renderCandidateName()}
        </Card>
      </CardContent>
    </Card>
  );
};
